import { GetPatientsBalanceResponse_PatientBalance } from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { Chips } from '@frontend/chips';
import { CollectPaymentMultiModalInstance } from '@frontend/payments-collection-flow';
import { REQUEST_ORIGINS } from '@frontend/person-invoice-create';
import { useAppScopeStore } from '@frontend/scope';
import { Button, TableColumnConfig, Text, useModalControl } from '@frontend/design-system';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';
import { GetLocationName } from '../../../utils/helpers';
import { PaymentsListContactName } from '../../PaymentsContactName/payments-list-contact-name';
import { BalanceTag } from './BalanceTag';
import { styles } from './styles';

export type CollectionColumn = GetPatientsBalanceResponse_PatientBalance;

export const generateColumns = (
  getLocationName: GetLocationName,
  showLocationName: boolean
): TableColumnConfig<CollectionColumn>[] => {
  return [
    {
      id: 'first_name',
      Header: 'Contact Name',
      accessor: (collection) => collection,
      cellRenderer: (data: GetPatientsBalanceResponse_PatientBalance) => (
        <PaymentsListContactName
          id={data.patientId}
          name={data.firstName + ' ' + data.lastName}
          locationId={data.locationIds?.[0]}
          hideTooltip={true}
          openContactOnNameClick={true}
        />
      ),
      sortType: (rowA, rowB) => {
        const nameA = `${rowA.original.firstName} ${rowA.original.lastName}`.toLowerCase();
        const nameB = `${rowB.original.firstName} ${rowB.original.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      },
    },
    {
      id: 'balance',
      Header: 'Balance',
      accessor: (collection) => collection?.balance,
      cellAlign: 'right',
      cellRenderer: (balance) => <Text size='inherit'>{formatCentsToCurrency(balance)}</Text>,
    },
    {
      id: 'location',
      Header: 'Location',
      accessor: (collection) => collection?.locationIds,
      cellRenderer: (locationIds) => (
        <div>
          {locationIds.map((locationId: string) => (
            <Chips.LocationChip key={locationId} css={styles.locationChip}>
              {getLocationName(locationId ?? '')}
            </Chips.LocationChip>
          ))}
        </div>
      ),
      disableSortBy: true,
      omit: !showLocationName,
    },
    {
      id: 'balance_type',
      Header: 'Balance Type',
      accessor: (collection) => collection.balanceType,
      cellRenderer: (balanceType) => <BalanceTag balanceType={balanceType} maxWidth='300px' />,
      disableSortBy: true,
    },
    {
      id: 'actions',
      Header: '',
      accessor: (collection) => collection.patientId,
      disableSortBy: true,
      cellRenderer: (id) => <Actions personId={id} />,
      width: 50,
      sticky: 'right',
      cellAlign: 'center',
    },
  ];
};

const Actions = ({ personId }: { personId: string }) => {
  const { selectedLocationIds } = useAppScopeStore();
  const { modalProps: createModalProps, triggerProps: createModalTriggerProps } = useModalControl();

  return (
    <>
      <div>
        <Button
          {...createModalTriggerProps}
          iconName='dollar-sign'
          variant='secondary'
          size='large'
          hoverLabel='Collect Payment'
          trackingId='collections-collect-payment-button'
        />
      </div>
      <CollectPaymentMultiModalInstance
        modalProps={createModalProps}
        collectProps={{
          personId,
          locationId: selectedLocationIds[0],
          flow: 'select',
          origin: REQUEST_ORIGINS.COLLECTIONS_MANUAL,
        }}
      />
    </>
  );
};
